import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { reduxApiClient } from 'infra'
import { ApiProvider } from 'Core'

const cloudProviderEndpoints = {
  AWS: 'awsresources',
  AZURE: 'azureresources',
  GCP: 'gcpresources'
}

export const fetchCloudResource = createAsyncThunk(
  'cloudResources/fetch',
  async ({ cloudType, resourceType }, { rejectWithValue }) => {
    try {
      const endpoint = cloudProviderEndpoints[cloudType.toUpperCase()]
      if (!endpoint) {
        throw new Error(`Invalid cloudType: ${cloudType}`)
      }
      const apiClient = reduxApiClient(endpoint)
      const data = await apiClient.getByDataParams({
        ['ObjectMeta.Namespace']: resourceType
      })
      return { resourceType: `${cloudType}_${resourceType}`, data: data || [] }
    } catch (err) {
      console.error(`Failed fetching resources for type ${resourceType}:`, err)
      return rejectWithValue(err.toString())
    }
  }
)

export const fetchChildResources = createAsyncThunk(
  'cloudResources/fetchChildResources',
  async ({ parentId }, { rejectWithValue }) => {
    try {
      const apiClient = reduxApiClient('awsresources')
      const data = await apiClient.getByDataParams({
        'Spec.Parent.RefID': parentId
      })
      return { parentId, data: data || [] }
    } catch (err) {
      console.error(`Failed fetching child resources for parent ${parentId}:`, err)
      return rejectWithValue(err.toString())
    }
  }
)

export const fetchAllCloudResources = createAsyncThunk(
  'cloudResources/fetchAll',
  async ({ cloudType }, { rejectWithValue }) => {
    console.log('fetchAllCloudResources thunk called with cloudType:', cloudType);
    try {
      const response = await new ApiProvider('resourcetypes')
        .setInstance({
          ObjectMeta: { Name: cloudType.toLowerCase() }
        })
        .get()

      if (!response?.data?.Types?.Elems) {
        throw new Error(`No types found for cloud type: ${cloudType}`)
      }

      const types = response.data.Types.Elems
      const endpoint = cloudProviderEndpoints[cloudType.toUpperCase()]
      const apiClient = reduxApiClient(endpoint)

      const allResources = []
      const batchSize = 5
      for (let i = 0; i < types.length; i += batchSize) {
        const batch = types.slice(i, i + batchSize)
        const fetchPromises = batch.map(async (type) => {
          try {
            const data = await apiClient.getByDataParams({
              ['ObjectMeta.Namespace']: type
            })
            return { resourceType: `${cloudType}_${type}`, data: data || [] }
          } catch (err) {
            console.error(`Failed fetching resources for type ${type}:`, err)
            return { resourceType: `${cloudType}_${type}`, data: [] }
          }
        })
        const batchResources = await Promise.all(fetchPromises)
        allResources.push(...batchResources)
        if (i + batchSize < types.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }

      const allData = allResources.reduce((acc, curr) => acc.concat(curr.data), [])
      return { cloudType, allData }
    } catch (err) {
      console.error('Error in fetchAllCloudResources:', err)
      return rejectWithValue(err.toString())
    }
  }
)

const cloudResourcesSlice = createSlice({
  name: 'cloudResources',
  initialState: {},
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllCloudResources.pending, (state, action) => {
        const { cloudType } = action.meta.arg
        state[`${cloudType}_all`] = {
          loading: true,
          loaded: false,
          data: [],
          error: null
        }
      })
      .addCase(fetchAllCloudResources.fulfilled, (state, action) => {
        const { cloudType, allData } = action.payload
        state[`${cloudType}_all`] = {
          loading: false,
          loaded: true,
          data: allData,
          error: null,
          lastFetched: Date.now()
        }
      })
      .addCase(fetchAllCloudResources.rejected, (state, action) => {
        const { cloudType } = action.meta.arg
        state[`${cloudType}_all`] = {
          loading: false,
          loaded: false,
          data: [],
          error: action.payload
        }
      })
      .addCase(fetchChildResources.pending, (state, action) => {
        const { parentId } = action.meta.arg
        state[`children_of_${parentId}`] = {
          loading: true,
          loaded: false,
          data: [],
          error: null
        }
      })
      .addCase(fetchChildResources.fulfilled, (state, action) => {
        const { parentId, data } = action.payload
        state[`children_of_${parentId}`] = {
          loading: false,
          loaded: true,
          data,
          error: null
        }
      })
      .addCase(fetchChildResources.rejected, (state, action) => {
        const { parentId } = action.meta.arg
        state[`children_of_${parentId}`] = {
          loading: false,
          loaded: false,
          data: [],
          error: action.payload
        }
      })
      .addCase(fetchCloudResource.pending, (state, action) => {
        const { resourceType } = action.meta.arg
        state[`${action.meta.arg.cloudType}_${resourceType}`] = {
          loading: true,
          loaded: false,
          data: [],
          error: null
        }
      })
      .addCase(fetchCloudResource.fulfilled, (state, action) => {
        state[action.payload.resourceType] = {
          loading: false,
          loaded: true,
          data: action.payload.data,
          error: null,
          lastFetched: Date.now(),
        };
      })
      .addCase(fetchCloudResource.rejected, (state, action) => {
        const { resourceType, cloudType } = action.meta.arg;
        state[`${cloudType}_${resourceType}`] = {
          loading: false,
          loaded: false,
          data: [],
          error: action.payload,
        };
      })
  }
})

export default cloudResourcesSlice.reducer
